import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Home from './pages/Home';
import Services from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import ServiceAreas from './pages/ServiceAreas';
import ServiceAreaDetail from './pages/ServiceAreaDetail';
import NeighborhoodDetail from './pages/NeighborhoodDetail';
import About from './pages/About';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import FAQ from './pages/FAQ';
import Gallery from './pages/Gallery';

// Dedicated, named, per-URL page components (exactly one component per route).
// These ensure each service / service-area URL renders its own dedicated
// component, NOT the Home component, NOT a catch-all.
import RodentControlPage from './pages/routes/RodentControlPage';
import BedBugPage from './pages/routes/BedBugPage';
import RoachPage from './pages/routes/RoachPage';
import BrooklynPage from './pages/routes/BrooklynPage';
import QueensPage from './pages/routes/QueensPage';
import BronxPage from './pages/routes/BronxPage';
import ManhattanPage from './pages/routes/ManhattanPage';

import { initAllAnalytics } from './utils/analytics';
import './App.css';

// Analytics wrapper component
const AnalyticsWrapper = ({ children }) => {
  useEffect(() => {
    // Initialize all analytics on app load
    initAllAnalytics();
  }, []);

  return children;
};

function App() {
  return (
    <Router>
      <AnalyticsWrapper>
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />

            {/* Explicit, dedicated, named page components per URL.
                Each of these renders its OWN component (NOT Home),
                with its own H1, title, meta description, body, FAQ, and CTAs.
                Listed BEFORE the dynamic /:slug route so they take precedence. */}
            <Route path="/services/rodent-control" element={<RodentControlPage />} />
            <Route path="/services/bed-bug-removal" element={<BedBugPage />} />
            <Route path="/services/cockroach-extermination" element={<RoachPage />} />

            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/service-areas" element={<ServiceAreas />} />
            {/* Two-part neighborhood URL (area/neighborhood) */}
            <Route path="/service-areas/:areaSlug/:neighborhoodSlug" element={<NeighborhoodDetail />} />

            {/* Explicit, dedicated page components per service-area URL */}
            <Route path="/service-areas/brooklyn-ny" element={<BrooklynPage />} />
            <Route path="/service-areas/queens-ny" element={<QueensPage />} />
            <Route path="/service-areas/bronx-ny" element={<BronxPage />} />
            <Route path="/service-areas/manhattan-ny" element={<ManhattanPage />} />

            {/* Combined slug for both areas and neighborhoods (handled by smart component) */}
            <Route path="/service-areas/:slug" element={<ServiceAreaDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/gallery" element={<Gallery />} />
            
            {/* Redirects for old Squarespace URLs */}
            <Route path="/blogs" element={<Navigate to="/blog" replace />} />
            <Route path="/blogs/:slug" element={<Navigate to="/blog" replace />} />
            <Route path="/faqs-4" element={<Navigate to="/faq" replace />} />
            <Route path="/home-1" element={<Navigate to="/" replace />} />
            
            {/* Redirects for root-level borough URLs */}
            <Route path="/queens-ny" element={<Navigate to="/service-areas/queens-ny" replace />} />
            <Route path="/brooklyn-ny" element={<Navigate to="/service-areas/brooklyn-ny" replace />} />
            <Route path="/manhattan-ny" element={<Navigate to="/service-areas/manhattan-ny" replace />} />
            <Route path="/bronx-ny" element={<Navigate to="/service-areas/bronx-ny" replace />} />
            <Route path="/staten-island-ny" element={<Navigate to="/service-areas/staten-island-ny" replace />} />
            <Route path="/nassau-county-ny" element={<Navigate to="/service-areas/nassau-county-ny" replace />} />
            <Route path="/suffolk-county-ny" element={<Navigate to="/service-areas/suffolk-county-ny" replace />} />
            <Route path="/westchester-ny" element={<Navigate to="/service-areas/westchester-ny" replace />} />
            <Route path="/hempstead-ny" element={<Navigate to="/service-areas/nassau-county-ny" replace />} />
            <Route path="/long-island-ny" element={<Navigate to="/service-areas/suffolk-county-ny" replace />} />
            
            {/* 404 fallback: show a NotFound message, do NOT redirect to Home.
                Redirecting unknown URLs to Home was masking real 404s as homepage hits,
                which is why service/area pages can appear to show "the homepage". */}
            <Route
              path="*"
              element={
                <div className="container mx-auto px-4 py-24 text-center">
                  <h1 className="text-4xl font-bold mb-4">Page not found</h1>
                  <p className="text-gray-600 mb-6">The page you&apos;re looking for doesn&apos;t exist.</p>
                  <a href="/" className="text-green-600 hover:underline">Return to Home</a>
                </div>
              }
            />
          </Routes>
        </Layout>
      </AnalyticsWrapper>
    </Router>
  );
}

export default App;